import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { Section, isSectionVisible } from "@/sections";
import { sectionRouteName } from "./routes";
import { parseIdUser } from "./params";

// Factories over their dependencies, so a guard that starts reading something
// new breaks the tests at compile time rather than at runtime.

const routeSection = (to: RouteLocationNormalized): Section | undefined =>
    to.meta.section ?? to.meta.requiresSection;

export type CustomerDeps<User extends { id_user: number }, Customer> = {
    store: {
        state: {
            user: User;
            userDetails: { customer: { id: number } | null };
        };
    };
    customerStore: {
        // The real store leaves this undefined until a user is opened.
        user: { id_user: number } | null | undefined;
        customer: unknown;
        open: (args: [User, Customer]) => void;
        setLoadError: (message: string | null) => void;
    };
    api: {
        customers: { get: (id: number) => Promise<Customer> };
    };
};

export const makeEnsureCustomer =
    <User extends { id_user: number }, Customer>({
        store,
        customerStore,
        api,
    }: CustomerDeps<User, Customer>) =>
    async (to: RouteLocationNormalized): Promise<boolean> => {
        if (routeSection(to) !== Section.Customer) {
            return true;
        }

        const idUser = parseIdUser(to.params.idUser);

        if (idUser === null) {
            return true;
        }

        if (customerStore.customer && customerStore.user?.id_user === idUser) {
            return true;
        }

        const customer = store.state.userDetails.customer;

        if (!customer) {
            return true;
        }

        try {
            const loaded = await api.customers.get(customer.id);

            if (store.state.user.id_user === idUser) {
                customerStore.open([store.state.user, loaded]);
            }
        } catch (e) {
            // Cancelling here would strand the admin on the previous section.
            if (store.state.user.id_user === idUser) {
                customerStore.setLoadError(
                    "Could not load the customer for this user."
                );
            }
        }

        return true;
    };

export type UserDeps = {
    store: {
        state: {
            user?: { id_user: number };
            loginUser?: { id_user: number };
        };
        dispatch: (action: string, payload?: unknown) => Promise<unknown>;
    };
};

export type SectionDeps = {
    store: Parameters<typeof isSectionVisible>[1];
    features: Parameters<typeof isSectionVisible>[2];
    access: Parameters<typeof isSectionVisible>[3];
};

export type DiscardDeps = {
    store: { getters: { hasBeenModified: boolean } };
    prompt: { confirm: () => Promise<boolean> };
};

// Also used by the header, whose buttons leave the app without routing.
export const confirmDiscard = ({
    store,
    prompt,
}: DiscardDeps): Promise<boolean> | boolean =>
    store.getters.hasBeenModified ? prompt.confirm() : true;

// Sections of the same user keep the edits on screen. Returning false cancels
// the navigation, which covers browser back too.
export const makeConfirmDiscard =
    (deps: DiscardDeps) =>
    async (
        to: RouteLocationNormalized,
        from: RouteLocationNormalized
    ): Promise<boolean> => {
        const fromIdUser = parseIdUser(from.params.idUser);
        const toIdUser = parseIdUser(to.params.idUser);

        if (
            fromIdUser === null ||
            toIdUser === null ||
            fromIdUser === toIdUser
        ) {
            return true;
        }

        return confirmDiscard(deps);
    };

export type BootResult =
    | { status: "ok"; user: { id_user: number } }
    | { status: "denied" }
    | { status: "error" };

// Global rather than a beforeEnter: vue-router skips beforeEnter when a record
// is reused and only the params change, which is the /users/1/x -> /users/2/x
// case this exists for.
export const makeEnsureUser =
    ({ store }: UserDeps) =>
    async (to: RouteLocationNormalized): Promise<true | RouteLocationRaw> => {
        if (to.name === "boot" || to.meta.public) {
            return true;
        }

        const idUser = parseIdUser(to.params.idUser);

        if (idUser === null) {
            return { name: "boot", replace: true };
        }

        if (store.state.user?.id_user === idUser) {
            return true;
        }

        try {
            await store.dispatch("loadUser", idUser);
        } catch (e) {
            // Gone, or not visible to us. Better to stay on whoever we have.
            const self = store.state.user?.id_user ?? store.state.loginUser?.id_user;

            if (self === undefined) {
                return { name: "denied", replace: true };
            }

            return {
                name: to.name as string,
                params: { ...to.params, idUser: self },
                replace: true,
            };
        }

        return true;
    };

export const makeRequireSection =
    ({ store, features, access }: SectionDeps) =>
    (to: RouteLocationNormalized): true | RouteLocationRaw => {
        const section = routeSection(to);

        if (section === undefined) {
            return true;
        }

        // isSectionVisible has nothing to read yet.
        if (!store.state.user) {
            return true;
        }

        if (isSectionVisible(section, store, features, access)) {
            return true;
        }

        return {
            name: sectionRouteName(Section.Overview),
            params: to.params,
            replace: true,
        };
    };

export const makeBootRedirect =
    ({ booted }: { booted: BootResult }) =>
    (to: RouteLocationNormalized): true | RouteLocationRaw => {
        if (booted.status !== "ok") {
            return to.name === booted.status
                ? true
                : { name: booted.status, replace: true };
        }

        if (to.name === "denied" || to.name === "error") {
            return { name: "boot", replace: true };
        }

        if (to.name === "boot") {
            return {
                name: sectionRouteName(Section.Overview),
                params: { idUser: booted.user.id_user },
                replace: true,
            };
        }

        return true;
    };
